import React, { useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';
import APIClient from '../../api';
import Loader from '../General/Loader';
import './MerchantCommissionDisclaimers.scss';

const BASE_PATH = '/merchant-commission-disclaimers';
const SOURCE_OPTIONS = ['impact', 'cj', 'rakuten', 'awin'];
const VIEW_OPTIONS = [
  { value: 'latest', label: 'Latest per program' },
  { value: 'history', label: 'All versions' }
];

const formatObservedAt = value => (value ? moment(value).format('MMM D, YYYY HH:mm') : '—');
const getStatus = row => (row.isCurrent ? 'current' : row.isAvailable ? 'superseded' : 'unavailable');
const getProgramLabel = row => row.merchantName || row.stable_merchant_id;
const formatRate = rate => (rate ? `${rate.value} ${rate.unit}${rate.currency ? ` ${rate.currency}` : ''}` : '—');

// Throwaway browser for the MerchantCommissionDisclaimers table: list on `/`, detail on `/:id`.
const MerchantCommissionDisclaimers = () => {
  const { id } = useParams();
  return <div className='merchant-commission-disclaimers-outer'>{id ? <DisclaimerDetail id={id} /> : <DisclaimerList />}</div>;
};

const DisclaimerList = () => {
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [limit, setLimit] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [source, setSource] = useState('');
  const [view, setView] = useState('latest');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let isStale = false;
    setIsLoading(true);
    const timeout = setTimeout(
      () => {
        APIClient.get('MerchantCommissionDisclaimers', { params: { source, view, search } })
          .then(response => {
            if (isStale) return;
            setRows(response.data.disclaimers);
            setLimit(response.data.limit);
          })
          .catch(() => window.ALERT.error('Failed to load commission disclaimers.'))
          .finally(() => !isStale && setIsLoading(false));
      },
      search ? 300 : 0
    );
    return () => {
      isStale = true;
      clearTimeout(timeout);
    };
  }, [source, view, search]);

  const columns = [
    { label: 'Source', render: row => row.source },
    {
      label: 'Program',
      className: 'program-cell',
      render: row => (
        <>
          <div className='primary'>{getProgramLabel(row)}</div>
          <div className='secondary'>{[row.stable_merchant_id, row.merchantDomain].filter(Boolean).join(' · ')}</div>
        </>
      )
    },
    { label: 'Version', render: row => `v${row.versionNumber}` },
    { label: 'Status', render: row => <StatusBadge row={row} /> },
    { label: 'Mode', render: row => row.disclaimerDisplay?.mode || '—' },
    { label: 'Disclaimer', className: 'disclaimer-cell', render: row => row.disclaimerDisplay?.disclaimer || '—' },
    { label: 'Last observed', render: row => formatObservedAt(row.lastObservedAt) }
  ];

  return (
    <>
      <div className='header-section'>
        <h1>Commission Disclaimers</h1>
        <div className='controls'>
          <input placeholder='Search program id, merchant name, or domain' value={search} onChange={e => setSearch(e.target.value)} />
          <select value={source} onChange={e => setSource(e.target.value)}>
            <option value=''>All sources</option>
            {SOURCE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <select value={view} onChange={e => setView(e.target.value)}>
            {VIEW_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {isLoading ? (
        <div className='loading-container'>
          <Loader />
        </div>
      ) : !rows.length ? (
        <div className='empty-state'>No commission disclaimers match these filters.</div>
      ) : (
        <div className='table-container'>
          <table>
            <thead>
              <tr>
                {columns.map(column => (
                  <th key={column.label}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className='clickable-row' onClick={() => history.push(`${BASE_PATH}/${row.id}`)}>
                  {columns.map(column => (
                    <td key={column.label} className={column.className}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === limit && <div className='limit-note'>Showing the first {limit} rows — narrow the filters to see more.</div>}
        </div>
      )}
    </>
  );
};

const DisclaimerDetail = ({ id }) => {
  const [disclaimer, setDisclaimer] = useState(null);
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFacts, setShowFacts] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setShowFacts(false);
    APIClient.get(`MerchantCommissionDisclaimers/${id}`)
      .then(response => {
        setDisclaimer(response.data.disclaimer);
        setVersions(response.data.versions);
      })
      .catch(() => window.ALERT.error('Failed to load this commission disclaimer.'))
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) {
    return (
      <div className='loading-container'>
        <Loader />
      </div>
    );
  }
  if (!disclaimer) return <div className='empty-state'>Commission disclaimer not found.</div>;

  const display = disclaimer.disclaimerDisplay;
  const previousId = disclaimer.previousMerchantCommissionDisclaimer_id;
  const metaFields = [
    { label: 'Version', value: `v${disclaimer.versionNumber}` },
    { label: 'Status', value: <StatusBadge row={disclaimer} /> },
    { label: 'Previous version', value: previousId ? <Link to={`${BASE_PATH}/${previousId}`}>#{previousId}</Link> : '—' },
    { label: 'Semantic hash', value: <code title={disclaimer.semanticHash}>{disclaimer.semanticHash}</code> },
    { label: 'Snapshot key', value: <code>{disclaimer.snapshotKey}</code> },
    { label: 'First observed', value: formatObservedAt(disclaimer.firstObservedAt) },
    { label: 'Last observed', value: formatObservedAt(disclaimer.lastObservedAt) },
    { label: 'Row id', value: `#${disclaimer.id}` }
  ];
  const displayFields = display
    ? [
        { label: 'Mode', value: display.mode },
        { label: 'Headline rate', value: formatRate(display.rate) },
        { label: 'Policy version', value: display.policyVersion },
        { label: 'As of', value: formatObservedAt(display.asOf) },
        { label: 'Rate basis', value: display.rateBasis },
        { label: 'Omitted facts', value: display.omittedFactCount || 0 },
        { label: 'Fallback reason', value: display.fallbackReason || '—' }
      ]
    : [];

  return (
    <>
      <Link to={BASE_PATH} className='back-link'>
        ← All commission disclaimers
      </Link>
      <div className='header-section'>
        <div>
          <h1>{getProgramLabel(disclaimer)}</h1>
          <div className='subtitle'>{[disclaimer.source, disclaimer.stable_merchant_id, disclaimer.merchantDomain].filter(Boolean).join(' · ')}</div>
        </div>
        <StatusBadge row={disclaimer} />
      </div>

      <div className='detail-grid'>
        <section className='card'>
          <h2>Version</h2>
          <dl>
            {metaFields.map(field => (
              <div key={field.label} className='field'>
                <dt>{field.label}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className='card'>
          <h2>Disclaimer display</h2>
          {display ? (
            <>
              <div className='disclaimer-text'>{display.disclaimer}</div>
              {!!display.lines?.length && (
                <ul className='lines'>
                  {display.lines.map(line => (
                    <li key={line.text}>
                      <span className='line-type'>{line.type}</span> {line.text}
                      <code className='rule-keys'>
                        {formatRate(line.rate)}
                        {!!line.ruleKeys?.length && ` · ${line.ruleKeys.join(', ')}`}
                      </code>
                    </li>
                  ))}
                </ul>
              )}
              <dl>
                {displayFields.map(field => (
                  <div key={field.label} className='field'>
                    <dt>{field.label}</dt>
                    <dd>{field.value}</dd>
                  </div>
                ))}
              </dl>
              {!!display.warnings?.length && (
                <ul className='warnings'>
                  {display.warnings.map(warning => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className='muted'>No disclaimer display — this version records the program as unavailable.</div>
          )}
        </section>
      </div>

      <section className='card'>
        <h2>Version history</h2>
        <table>
          <thead>
            <tr>
              <th>Version</th>
              <th>Status</th>
              <th>Mode</th>
              <th>Disclaimer</th>
              <th>Hash</th>
              <th>First observed</th>
              <th>Last observed</th>
            </tr>
          </thead>
          <tbody>
            {versions.map(version => (
              <tr key={version.id} className={cn({ active: version.id === disclaimer.id })}>
                <td>
                  <Link to={`${BASE_PATH}/${version.id}`}>v{version.versionNumber}</Link>
                </td>
                <td>
                  <StatusBadge row={version} />
                </td>
                <td>{version.disclaimerDisplay?.mode || '—'}</td>
                <td className='disclaimer-cell'>{version.disclaimerDisplay?.disclaimer || '—'}</td>
                <td>
                  <code title={version.semanticHash}>{version.semanticHash.slice(0, 12)}</code>
                </td>
                <td>{formatObservedAt(version.firstObservedAt)}</td>
                <td>{formatObservedAt(version.lastObservedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className='card'>
        <div className='card-header'>
          <h2>Rate facts ({disclaimer.rateFacts.length})</h2>
          <button onClick={() => setShowFacts(!showFacts)}>{showFacts ? 'Hide' : 'Show'}</button>
        </div>
        {showFacts && <pre>{JSON.stringify(disclaimer.rateFacts, null, 2)}</pre>}
      </section>
    </>
  );
};

const StatusBadge = ({ row }) => {
  const status = getStatus(row);
  return <span className={cn('status-badge', status)}>{status}</span>;
};

export default MerchantCommissionDisclaimers;
